// Feedback drafts (Lane 1331, Phase 2 slices 2C and 2D). A starter draft is
// built by the server from facts about that supplier's own offer (parts not
// quoted, parts marked no bid) or from the buyer's own part list; the buyer
// then writes, edits or discards drafts here. Nothing on this screen is sent:
// feedback reaches a supplier only from the Round tab, after a review.
import React, { useState } from 'react';
import { formatDueAt } from '../sourcing-portal/portalView';
import { formatUnitPrice } from './sourcingApi';

const LINE_SOURCE_TEXT = { not_quoted: 'Not quoted yet', no_bid: 'Marked no bid', buyer: 'You asked',
  negotiation: 'From the Negotiation tab' };

export const NOT_SENT_TEXT = 'Nothing is sent until you choose Send feedback in Rounds and feedback.';

// What each feedback line asks, in the buyer's words.
export const FLAG_LABEL = {
  please_quote: 'Please quote', review_price: 'Review price', review_lead_time: 'Review lead time', note: 'Note',
};
const FLAGS = Object.keys(FLAG_LABEL);
const MESSAGE_MAX = 8000;
const NOTE_MAX = 1000;
const PART_MAX = 50;
const TARGET_MAX = 1000000000;
const EMPTY_LINE = { part_number: '', flag: 'review_price', note: '', target_price: '' };

export function DraftFeedbackForm({ api, eventId, supplier, onDone, onCancel }) {
  const [notQuoted, setNotQuoted] = useState(false);
  const [noBid, setNoBid] = useState(false);
  const [parts, setParts] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(null);

  const partList = parts.split(/\r?\n/).map((p) => p.trim()).filter(Boolean);
  const nothing = !notQuoted && !noBid && partList.length === 0;

  const submit = async () => {
    setBusy(true);
    setError('');
    setSaved(null);
    const selectors = [];
    if (notQuoted) selectors.push('not_quoted');
    if (noBid) selectors.push('no_bid');
    try {
      const r = await api.draftFeedback(eventId, {
        supplier_id: supplier.supplier_id, selectors,
        ...(partList.length ? { part_numbers: partList } : {}),
      });
      setSaved(r.result);
      if (onDone) onDone(r.result);
    } catch (e) {
      const unknown = e.code === 'unknown_parts' && e.detail && Array.isArray(e.detail.parts)
        ? ` ${e.detail.parts.join(', ')}` : '';
      setError(`${e.message}${unknown}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="srce-fb">
      <span className="srce-label">Draft feedback for {supplier.supplier_name}</span>
      <label className="srce-inline">
        <input type="checkbox" checked={notQuoted} onChange={(e) => setNotQuoted(e.target.checked)} />
        Parts not quoted yet
      </label>
      <label className="srce-inline">
        <input type="checkbox" checked={noBid} onChange={(e) => setNoBid(e.target.checked)} />
        Parts marked no bid
      </label>
      <label className="srce-label" htmlFor={`srce-fb-${supplier.supplier_id}`}>
        Ask them to review the price of these parts (optional, one part number per line)
      </label>
      <textarea id={`srce-fb-${supplier.supplier_id}`} className="srce-textarea" rows={3} value={parts}
        onChange={(e) => setParts(e.target.value)} />
      <div className="srce-mut">{NOT_SENT_TEXT}</div>
      <div className="srce-actions">
        <button type="button" className="srce-btn srce-btn--primary" disabled={busy || nothing} onClick={submit}>
          {busy ? 'Saving…' : 'Save draft'}</button>
        <button type="button" className="ll-clr" onClick={onCancel}>Close</button>
      </div>
      {saved && (
        <div className="srce-good" role="status">
          Draft saved: {Number(saved.lines || 0).toLocaleString()} {saved.lines === 1 ? 'part' : 'parts'}. Nothing is sent.
        </div>
      )}
      {error && <div className="srce-crit" role="alert">{error}</div>}
    </div>
  );
}

// ---- The feedback editor (slice 2D) -------------------------------------------

// A target price as typed: digits with an optional dot and up to 4 decimals.
function targetError(text) {
  const t = text.trim();
  if (!t) return '';
  if (!/^\d+(\.\d+)?$/.test(t)) return 'Enter the target price as a number, like 12.50.';
  if (/\.\d{5,}$/.test(t)) return 'Use at most 4 decimals in the target price.';
  if (!(Number(t) > 0)) return 'The target price must be more than 0.';
  if (Number(t) > TARGET_MAX) return 'The target price is too large.';
  return '';
}

function linesFromServer(lines) {
  return (lines || []).map((l) => ({
    part_number: l.part_number || '',
    flag: FLAGS.includes(l.flag) ? l.flag : 'note',
    note: l.note || '',
    target_price: l.target_price === null || l.target_price === undefined ? '' : String(l.target_price),
  }));
}

// The form's lines, checked. Blank rows are dropped; the first problem is
// returned as `error` so the screen can say what to fix.
function cleanLines(rows) {
  const out = [];
  const seen = new Set();
  for (const r of rows) {
    const pn = r.part_number.trim();
    const note = r.note.trim();
    const target = r.target_price.trim();
    if (!pn && !note && !target) continue;
    if (!pn) return { error: 'Enter a part number on every line, or remove the line.' };
    if (seen.has(pn)) return { error: `Part ${pn} is listed twice.` };
    seen.add(pn);
    const bad = targetError(target);
    if (bad) return { error: `${pn}: ${bad}` };
    out.push({ part_number: pn, flag: r.flag, ...(note ? { note } : {}), ...(target ? { target_price: target } : {}) });
  }
  return { lines: out };
}

function feedbackErrorText(e) {
  if (e.code === 'unknown_parts') {
    const parts = e.detail && Array.isArray(e.detail.parts) ? e.detail.parts : [];
    return `These parts are not in this event or are listed twice: ${parts.join(', ')}.`;
  }
  if (e.code === 'disclosure_not_draft') return 'This feedback was already sent or discarded, so it cannot change.';
  if (e.code === 'disclosure_not_found') return 'This feedback draft no longer exists.';
  if (e.code === 'supplier_not_found') return 'This supplier is no longer in the event.';
  if (e.code === 'event_not_editable') return 'This event can no longer change.';
  return e.message;
}

function FeedbackForm({ title, initial, suppliers, currency, onSave, onCancel }) {
  const [supplierId, setSupplierId] = useState(initial.supplier_id ? String(initial.supplier_id) : '');
  const [message, setMessage] = useState(initial.message || '');
  const [rows, setRows] = useState(() => {
    const start = linesFromServer(initial.lines);
    return start.length ? start : [{ ...EMPTY_LINE }];
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isNew = !initial.disclosure_id;

  const setRow = (i, field, value) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, [field]: value } : r)));

  const save = async () => {
    setError('');
    if (isNew && !supplierId) { setError('Choose the supplier.'); return; }
    if (!message.trim()) { setError('Write a message to the supplier.'); return; }
    const { lines, error: lineError } = cleanLines(rows);
    if (lineError) { setError(lineError); return; }
    setBusy(true);
    try {
      await onSave({ supplier_id: Number(supplierId), message: message.trim(), lines });
    } catch (e) {
      setError(feedbackErrorText(e));
      setBusy(false);
    }
  };

  const idBase = initial.disclosure_id || 'new';
  return (
    <div className="srce-fbform">
      <span className="srce-label">{title}</span>
      {isNew && (
        <div className="srce-field">
          <label className="srce-label" htmlFor={`srce-fbs-${idBase}`}>Supplier</label>
          <select id={`srce-fbs-${idBase}`} className="srce-input srce-input--wide" value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">Choose a supplier</option>
            {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</option>)}
          </select>
        </div>
      )}
      <div className="srce-field">
        <label className="srce-label" htmlFor={`srce-fbm-${idBase}`}>Message to the supplier</label>
        <textarea id={`srce-fbm-${idBase}`} className="srce-textarea" rows={4} value={message} maxLength={MESSAGE_MAX}
          placeholder="For example: Thank you for your quote. Please look at the parts listed below."
          onChange={(e) => setMessage(e.target.value)} />
      </div>
      <div className="srce-field">
        <span className="srce-label">Parts</span>
        <div className="srce-scroll">
          <table className="srce-grid srce-fblines">
            <thead>
              <tr><th>Part number</th><th>What to review</th><th>Note (optional)</th>
                <th className="srce-r">Target price{currency ? ` (${currency})` : ''} (optional)</th><th><span className="srce-sr">Remove</span></th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td><input className="srce-input srce-mono" value={r.part_number} maxLength={PART_MAX}
                    aria-label={`Part number, line ${i + 1}`} onChange={(e) => setRow(i, 'part_number', e.target.value)} /></td>
                  <td>
                    <select className="srce-input" value={r.flag} aria-label={`What to review, line ${i + 1}`}
                      onChange={(e) => setRow(i, 'flag', e.target.value)}>
                      {FLAGS.map((f) => <option key={f} value={f}>{FLAG_LABEL[f]}</option>)}
                    </select>
                  </td>
                  <td><input className="srce-input srce-fblines-note" value={r.note} maxLength={NOTE_MAX}
                    aria-label={`Note, line ${i + 1}`} onChange={(e) => setRow(i, 'note', e.target.value)} /></td>
                  <td className="srce-r"><input className="srce-input srce-input--num" inputMode="decimal" value={r.target_price}
                    aria-label={`Target price, line ${i + 1}`} aria-invalid={targetError(r.target_price) ? true : undefined}
                    onChange={(e) => setRow(i, 'target_price', e.target.value)} /></td>
                  <td><button type="button" className="ll-clr" aria-label={`Remove line ${i + 1}`}
                    onClick={() => setRows((rs) => rs.filter((_, k) => k !== i))}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="srce-actions srce-gap">
          <button type="button" className="srce-btn" onClick={() => setRows((rs) => [...rs, { ...EMPTY_LINE }])}>Add a part</button>
          <span className="srce-mut">A target price is shown to this supplier only if you add one.</span>
        </div>
      </div>
      <div className="srce-mut">{NOT_SENT_TEXT}</div>
      {error && <div className="srce-crit" role="alert">{error}</div>}
      <div className="srce-actions">
        <button type="button" className="srce-btn srce-btn--primary" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save draft'}</button>
        <button type="button" className="ll-clr" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function FeedbackLines({ lines, currency, showSource }) {
  if (!lines || lines.length === 0) return <div className="srce-mut">No parts listed.</div>;
  return (
    <ul className="srce-plainlist">
      {lines.map((l) => (
        <li key={l.part_number}>
          <span className="srce-mono">{l.part_number}</span>: {l.text || FLAG_LABEL[l.flag] || ''}
          {l.target_price !== null && l.target_price !== undefined && l.target_price !== '' && (
            <span> Target price {formatUnitPrice(l.target_price, currency)}.</span>
          )}
          {showSource && l.source && <span className="srce-sub"> ({LINE_SOURCE_TEXT[l.source] || l.source})</span>}
          {l.note && <div className="srce-note-body">{l.note}</div>}
        </li>
      ))}
    </ul>
  );
}

// What a published change removed from this draft (Lane 1354, consult #6): target
// prices of parts whose unit or the currency changed, and lines of removed parts.
function RebaseNotices({ notices }) {
  if (!notices || notices.length === 0) return null;
  return (
    <div className="srce-warnbox" role="status">
      {notices.map((n, i) => {
        if (n.earlier) {
          return <div key={`n-${i}`}>Earlier changes removed {n.count.toLocaleString()} target prices or lines.</div>;
        }
        const change = n.amendment_number ? `Change ${n.amendment_number}` : 'A change';
        return (
          <div key={`n-${i}`}>
            {n.cleared_targets && n.cleared_targets.length > 0 && (
              <div>{change} removed the target price on {n.cleared_targets.join(', ')}
                {n.currency ? ' (the currency changed)' : ' (the unit changed or the part was added)'}. Set it again if you still want it.</div>
            )}
            {n.removed_lines && n.removed_lines.length > 0 && (
              <div>{change} removed the lines for {n.removed_lines.join(', ')} (no longer in the event).</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// A save the server refused because the draft or the event changed since the form
// opened (Lane 1354): the form closes, so the next edit starts from what is saved now
// and carries that version's digests (Codex S3c r1 #1).
const STALE_EDIT = ['feedback_changed', 'scope_changed'];

function DraftCard({ d, currency, editable, suppliers, onSave, onDiscard, currentRound, scopeVersion }) {
  // A draft from an earlier round can no longer be sent (the server refuses
  // it); it can still be discarded.
  const stale = Boolean(currentRound) && d.round < currentRound;
  const [editing, setEditing] = useState(false);
  // What the form was opened on: the draft's content digest and the event's scope
  // version at that moment, never the refreshed ones.
  const [opened, setOpened] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (editing) {
    return (
      <div className="srce-invsup">
        <FeedbackForm title={`Edit feedback for ${d.supplier_name}`} initial={{ ...d, disclosure_id: d.public_id }}
          suppliers={suppliers} currency={currency} onCancel={() => setEditing(false)}
          onSave={async (input) => {
            try {
              await onSave(d, input, opened);
              setEditing(false);
            } catch (e) {
              if (!STALE_EDIT.includes(e.code)) throw e;
              setEditing(false);
              setError(e.message);
            }
          }} />
      </div>
    );
  }
  const discard = async () => {
    setBusy(true);
    setError('');
    try {
      await onDiscard(d);
    } catch (e) {
      setError(feedbackErrorText(e));
      setBusy(false);
      setConfirming(false);
    }
  };
  return (
    <div className="srce-invsup">
      <div className="srce-actions">
        <span className="srce-strong">{d.supplier_name}</span>
        <span className="srce-status srce-status--draft">Draft, not sent</span>
        {d.source === 'chat' && <span className="srce-tag">Drafted in chat</span>}
        <span className="srce-mut">Round {d.round}, written {formatDueAt(d.created_at)}</span>
        {stale && <span className="srce-warn">From an earlier round: it cannot be sent now. Discard it, or write new feedback for this round.</span>}
        {editable && !confirming && (
          <span className="srce-push srce-row">
            {!stale && (
              <button type="button" className="ll-clr" onClick={() => {
                setOpened({ digest: d.content_digest, scope: scopeVersion });
                setError('');
                setEditing(true);
              }}>Edit</button>
            )}
            <button type="button" className="ll-clr" onClick={() => setConfirming(true)}>Discard</button>
          </span>
        )}
        {editable && confirming && (
          <span className="srce-push srce-row">
            <span className="srce-crit">Discard this draft? It is never sent.</span>
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy} onClick={discard}>Yes, discard</button>
            <button type="button" className="ll-clr" disabled={busy} onClick={() => setConfirming(false)}>Keep it</button>
          </span>
        )}
      </div>
      <RebaseNotices notices={d.rebase_notices} />
      {d.message && <div className="srce-note-body">{d.message}</div>}
      <FeedbackLines lines={d.lines} currency={currency} showSource />
      {error && <div className="srce-crit" role="alert">{error}</div>}
    </div>
  );
}

// Every feedback draft of the event, grouped by state: drafts to edit or
// discard, then feedback already sent (read-only, it never changes).
export function FeedbackEditor({ api, eventId, items, suppliers, currency, editable, onChanged, onOpenRound, currentRound, scopeVersion }) {
  // The event's scope version when "New feedback" opened (null: not creating).
  const [creating, setCreating] = useState(null);
  const all = items || [];
  const drafts = all.filter((d) => d.state === 'draft');
  const released = all.filter((d) => d.state === 'released');
  const discarded = all.filter((d) => d.state === 'discarded').length;

  // After any refusal that means the list is out of date, reload it.
  const run = async (action, input) => {
    try {
      await api.feedbackAction(eventId, action, input);
      await onChanged();
    } catch (e) {
      // feedback_changed: the draft changed since it was loaded (Lane 1354): reload it
      // so the next edit starts from what is saved now.
      if (['disclosure_not_draft', 'disclosure_not_found', 'feedback_changed', 'scope_changed'].includes(e.code)) onChanged();
      throw e;
    }
  };

  return (
    <div>
      <div className="srce-actions">
        <h2 className="srce-h2">Feedback to suppliers</h2>
        {editable && creating === null && (
          <button type="button" className="srce-btn srce-push" disabled={suppliers.length === 0}
            onClick={() => setCreating(scopeVersion || 0)}>New feedback</button>
        )}
      </div>
      <div className="srce-mut">
        {NOT_SENT_TEXT}
        {onOpenRound && <> <button type="button" className="ll-clr" onClick={onOpenRound}>Go to the Round tab</button></>}
      </div>
      {creating !== null && (
        <div className="srce-invsup">
          <FeedbackForm title="New feedback" initial={{}} suppliers={suppliers} currency={currency}
            onCancel={() => setCreating(null)}
            onSave={async (input) => {
              await run('create_feedback', { ...input, expected_scope_version: creating || undefined });
              setCreating(null);
            }} />
        </div>
      )}
      {items === null && <div className="ll-loading">Loading feedback…</div>}
      {items && drafts.length === 0 && released.length === 0 && creating === null && (
        <div className="srce-mut srce-gap">No feedback yet.</div>
      )}
      {drafts.map((d) => (
        <DraftCard key={d.public_id} d={d} currency={currency} editable={editable} suppliers={suppliers}
          currentRound={currentRound} scopeVersion={scopeVersion}
          onSave={(draft, input, opened) => run('update_feedback', { disclosure_id: draft.public_id,
            expected_content_digest: opened.digest, expected_scope_version: opened.scope,
            message: input.message, lines: input.lines })}
          onDiscard={(draft) => run('discard_feedback', { disclosure_id: draft.public_id })} />
      ))}
      {released.length > 0 && (
        <>
          <h2 className="srce-h2">Feedback sent</h2>
          <div className="srce-mut">Sent feedback never changes.</div>
          {released.map((d) => (
            <div key={d.public_id} className="srce-invsup">
              <div className="srce-actions">
                <span className="srce-strong">{d.supplier_name}</span>
                <span className="srce-status srce-status--open">Sent</span>
                <span className="srce-mut">Round {d.round}, sent {formatDueAt(d.released_at)}</span>
              </div>
              {d.message && <div className="srce-note-body">{d.message}</div>}
              <FeedbackLines lines={d.lines} currency={currency} />
            </div>
          ))}
        </>
      )}
      {discarded > 0 && (
        <div className="srce-mut srce-gap">
          {discarded === 1 ? 'One discarded draft is not shown.' : `${discarded.toLocaleString()} discarded drafts are not shown.`}
        </div>
      )}
    </div>
  );
}
